import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ImagesEditor } from "@/pages/admin/ImagesEditor";
import { OptionsEditor } from "@/pages/admin/OptionsEditor";

const input = "h-11 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm outline-none focus:border-[#0066FF]";
const Field = ({ label, children }) => <label className="block"><span className="mb-1.5 block text-xs text-slate-400">{label}</span>{children}</label>;
const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3 text-sm">{label}<Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} /></label>
);
const Card = ({ title, children }) => <section className="rounded-2xl border border-white/10 bg-[#12141A] p-5"><h2 className="mb-4 font-display font-semibold">{title}</h2>{children}</section>;

export default function AdminProductEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
    api.get("/admin/categories").then((r) => setCats(r.data));
  }, [id]);
  if (p === false) return <p className="text-slate-400">Prodotto non trovato.</p>;
  if (!p) return <div className="h-60 animate-pulse rounded-2xl bg-white/5" />;

  const set = (k) => (v) => setP({ ...p, [k]: v?.target ? v.target.value : v });
  const numv = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const save = async () => {
    setSaving(true);
    const { name, sku, description, category_path, ribbon, subtitle, featured, available, inventory_tracked, images, options, shipping_type, shipping_required } = p;
    try {
      const { data } = await api.put(`/admin/products/${id}`, { name, sku, description, category_path, ribbon, subtitle, featured, available, inventory_tracked, images, options, shipping_type, shipping_required,
        price: numv(p.price) || 0, compare_price: numv(p.compare_price), quantity: Math.trunc(numv(p.quantity) || 0), shipping_fixed_rate: numv(p.shipping_fixed_rate) || 0 });
      setP(data);
      toast.success("Prodotto salvato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo prodotto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Prodotto eliminato");
    nav("/admin/prodotti");
  };

  return (
    <div className="max-w-5xl" data-testid="admin-product-edit">
      <Link to="/admin/prodotti" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ChevronLeft className="h-4 w-4" />Prodotti</Link>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold">{p.name}</h1>
        <div className="flex gap-2">
          <button onClick={remove} className="h-10 rounded-full border border-red-500/40 px-5 text-sm text-red-300" data-testid="admin-product-delete">Elimina</button>
          <button onClick={save} disabled={saving} className="h-10 rounded-full bg-[#0066FF] px-6 text-sm font-medium disabled:opacity-50" data-testid="admin-product-save">{saving ? "Salvataggio…" : "Salva"}</button>
        </div>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <Card title="Dettagli">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Field label="Nome"><input className={input} value={p.name} onChange={set("name")} data-testid="edit-name" /></Field></div>
              <Field label="Codice (SKU)"><input className={input} value={p.sku || ""} onChange={set("sku")} data-testid="edit-sku" /></Field>
              <Field label="Categoria">
                <select className={input + " bg-[#12141A]"} value={p.category_path || ""} onChange={set("category_path")} data-testid="edit-category">
                  <option value="">— Nessuna —</option>{cats.map((c) => <option key={c.path} value={c.path}>{c.path}</option>)}
                </select>
              </Field>
              <Field label="Etichetta (ribbon)"><input className={input} value={p.ribbon || ""} onChange={set("ribbon")} data-testid="edit-ribbon" /></Field>
              <Field label="Sottotitolo"><input className={input} value={p.subtitle || ""} onChange={set("subtitle")} data-testid="edit-subtitle" /></Field>
            </div>
            <Field label="Descrizione (HTML)"><textarea rows={8} className={input + " mt-4 h-auto py-3 font-mono text-xs"} value={p.description || ""} onChange={set("description")} data-testid="edit-description" /></Field>
          </Card>
          <Card title="Immagini"><ImagesEditor images={p.images || []} onChange={set("images")} /></Card>
          <Card title="Varianti e opzioni"><OptionsEditor options={p.options || []} onChange={set("options")} /></Card>
        </div>
        <div className="space-y-6">
          <Card title="Prezzo">
            <div className="space-y-4">
              <Field label="Prezzo (€)"><input type="number" step="0.01" className={input} value={p.price ?? ""} onChange={set("price")} data-testid="edit-price" /></Field>
              <Field label="Prezzo barrato (€)"><input type="number" step="0.01" className={input} value={p.compare_price ?? ""} onChange={set("compare_price")} data-testid="edit-compare-price" /></Field>
            </div>
          </Card>
          <Card title="Disponibilità">
            <div className="space-y-3">
              <Toggle label="Visibile nel negozio" checked={p.available} onChange={set("available")} testid="edit-available" />
              <Toggle label="In evidenza" checked={p.featured} onChange={set("featured")} testid="edit-featured" />
              <Toggle label="Gestisci quantità" checked={p.inventory_tracked} onChange={set("inventory_tracked")} testid="edit-inventory-tracked" />
              {p.inventory_tracked && <Field label="Quantità in magazzino"><input type="number" className={input} value={p.quantity ?? 0} onChange={set("quantity")} data-testid="edit-quantity" /></Field>}
            </div>
          </Card>
          <Card title="Spedizione">
            <div className="space-y-3">
              <Toggle label="Richiede spedizione" checked={p.shipping_required} onChange={set("shipping_required")} testid="edit-shipping-required" />
              <Field label="Tipo">
                <select className={input + " bg-[#12141A]"} value={p.shipping_type || "GLOBAL_METHODS"} onChange={set("shipping_type")} data-testid="edit-shipping-type">
                  <option value="GLOBAL_METHODS">Metodi del negozio</option><option value="FREE_SHIPPING">Spedizione gratuita</option><option value="SELECTED_METHODS">Metodi selezionati</option>
                </select>
              </Field>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
